import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import { anomalyApi, DetectedAnomaly, AnomalyStatistics, AnomalyConfig, AnomalyAIAnalysis } from '../../services/api/anomaly.api';
import {
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  SparklesIcon,
  Cog6ToothIcon,
  PlayIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { Fragment } from 'react';

const SEVERITY_COLORS = {
  critical: '#EF4444',
  high: '#F97316',
  medium: '#EAB308',
  low: '#3B82F6',
};

const TYPE_COLORS = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1', '#14B8A6'];

export default function AnomalyDetectionPage() {
  const { t } = useTranslation('apm');
  const { t: tCommon } = useTranslation('common');

  const selectedAppId = useAppSelector((state: any) => state.application?.selectedApplicationId) || 'all';

  const [anomalies, setAnomalies] = useState<DetectedAnomaly[]>([]);
  const [statistics, setStatistics] = useState<AnomalyStatistics | null>(null);
  const [config, setConfig] = useState<AnomalyConfig | null>(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState<DetectedAnomaly | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [running, setRunning] = useState(false);

  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '7d',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const appId = selectedAppId === 'all' ? undefined : selectedAppId;

      const [anomaliesRes, statsRes, configRes] = await Promise.all([
        anomalyApi.getAnomalies({ applicationId: appId, limit: 100 }),
        anomalyApi.getStatistics({
          applicationId: appId,
          startTime: timeRange.startTime,
          endTime: timeRange.endTime,
        }),
        anomalyApi.getConfig(appId),
      ]);

      setAnomalies(anomaliesRes.data || []);
      setStatistics(statsRes);
      setConfig(configRes);
    } catch (error) {
      console.error('Error loading anomaly data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedAppId, timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRunDetection = async () => {
    setRunning(true);
    try {
      const appId = selectedAppId === 'all' ? undefined : selectedAppId;
      const result = await anomalyApi.runDetection(appId);

      if (result.anomaliesDetected > 0) {
        loadData();
      }
    } catch (error) {
      console.error('Error running detection:', error);
    } finally {
      setRunning(false);
    }
  };

  const handleAnalyzeAnomaly = async (anomaly: DetectedAnomaly) => {
    setSelectedAnomaly(anomaly);
    setShowDetailsModal(true);

    if (!anomaly.aiAnalysis) {
      setAnalyzing(true);
      try {
        const result = await anomalyApi.analyzeAnomaly(anomaly.id);
        setSelectedAnomaly(prev => prev ? { ...prev, aiAnalysis: result.analysis } : null);
      } catch (error) {
        console.error('Error analyzing anomaly:', error);
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const handleSaveConfig = async (newConfig: Partial<AnomalyConfig>) => {
    try {
      const updated = await anomalyApi.updateConfig(newConfig);
      setConfig(updated);
      setShowConfigModal(false);
    } catch (error) {
      console.error('Error saving config:', error);
    }
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      response_time: t('anomaly.types.responseTime', 'Response Time'),
      error_rate: t('anomaly.types.errorRate', 'Error Rate'),
      throughput: t('anomaly.types.throughput', 'Throughput'),
      latency_p99: t('anomaly.types.latencyP99', 'P99 Latency'),
      slow_query: t('anomaly.types.slowQuery', 'Slow Query'),
      error_spike: t('anomaly.types.errorSpike', 'Error Spike'),
    };
    return labels[type] || type;
  };

  const getMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      zscore: 'Z-Score',
      moving_avg: t('anomaly.methods.movingAvg', 'Moving Avg'),
      percentile: t('anomaly.methods.percentile', 'Percentile'),
      ewma: 'EWMA',
      threshold: t('anomaly.methods.threshold', 'Threshold'),
    };
    return labels[method] || method;
  };

  const severityData = statistics?.bySeverity
    ? Object.entries(statistics.bySeverity).map(([name, value]) => ({
        name,
        value,
        color: SEVERITY_COLORS[name as keyof typeof SEVERITY_COLORS] || '#6B7280',
      }))
    : [];

  const typeData = statistics?.byType
    ? Object.entries(statistics.byType).map(([name, value], index) => ({
        name: getTypeLabel(name),
        value,
        color: TYPE_COLORS[index % TYPE_COLORS.length],
      }))
    : [];

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('anomaly.title', 'APM Anomaly Detection')}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('anomaly.subtitle', 'Statistical detection of anomalies with AI-powered analysis')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <button
              onClick={() => setShowConfigModal(true)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {tCommon('settings')}
            </button>
            <button
              onClick={handleRunDetection}
              disabled={running}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {running ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
              {t('anomaly.runDetection', 'Run Detection')}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <ExclamationTriangleIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('anomaly.stats.total', 'Total Anomalies')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {statistics?.totalAnomalies || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('anomaly.stats.critical', 'Critical')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {statistics?.bySeverity?.critical || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <ChartBarIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('anomaly.stats.high', 'High Severity')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {statistics?.bySeverity?.high || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                <CpuChipIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('anomaly.stats.detection', 'Detection Enabled')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {config?.enabled ? tCommon('yes') : tCommon('no')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Trend Chart */}
          <div className="col-span-2 rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              {t('anomaly.charts.trend', 'Anomaly Trend')}
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statistics?.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(17, 24, 39, 0.9)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                  />
                  <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Severity Distribution */}
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              {t('anomaly.charts.bySeverity', 'By Severity')}
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={severityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {severityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Anomaly List */}
        <div className="rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('anomaly.recentAnomalies', 'Recent Anomalies')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : anomalies.length === 0 ? (
              <div className="py-12 text-center">
                <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('anomaly.noAnomalies', 'No anomalies detected in this period')}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.time', 'Time')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.type', 'Type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.severity', 'Severity')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.value', 'Value')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.deviation', 'Deviation')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.method', 'Method')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('anomaly.table.ai', 'AI')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {anomalies.map((anomaly) => (
                    <tr
                      key={anomaly.id}
                      onClick={() => handleAnalyzeAnomaly(anomaly)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {new Date(anomaly.timestamp).toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {getTypeLabel(anomaly.type)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getSeverityBadgeClass(anomaly.severity)}`}>
                          {anomaly.severity}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {anomaly.currentValue.toFixed(2)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <span className={anomaly.deviationPercent > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                          {anomaly.deviationPercent > 0 ? '+' : ''}{anomaly.deviationPercent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {getMethodLabel(anomaly.detectionMethod)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {anomaly.aiAnalysis ? (
                          <SparklesIcon className="h-5 w-5 text-purple-500" />
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Anomaly Details Modal */}
        <Transition appear show={showDetailsModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowDetailsModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-gray-800">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                      {t('anomaly.details.title', 'Anomaly Details')}
                    </Dialog.Title>

                    {selectedAnomaly && (
                      <div className="mt-4 space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.type', 'Type')}
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {getTypeLabel(selectedAnomaly.type)}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.severity', 'Severity')}
                            </label>
                            <p className={`text-sm font-medium ${getSeverityBadgeClass(selectedAnomaly.severity)} inline-flex rounded-full px-2 py-0.5`}>
                              {selectedAnomaly.severity}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.currentValue', 'Current Value')}
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {selectedAnomaly.currentValue.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.expectedValue', 'Expected Value')}
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {selectedAnomaly.expectedValue.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.deviation', 'Deviation')}
                            </label>
                            <p className={`text-sm ${selectedAnomaly.deviationPercent > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {selectedAnomaly.deviationPercent > 0 ? '+' : ''}{selectedAnomaly.deviationPercent.toFixed(1)}%
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              {t('anomaly.details.method', 'Detection Method')}
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {getMethodLabel(selectedAnomaly.detectionMethod)}
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('anomaly.details.description', 'Description')}
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedAnomaly.description}
                          </p>
                        </div>

                        {/* Baseline Context */}
                        {selectedAnomaly.context?.baseline && (
                          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
                            <h4 className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                              {t('anomaly.details.baseline', 'Baseline Statistics')}
                            </h4>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Mean:</span>{' '}
                                <span className="text-gray-900 dark:text-white">{selectedAnomaly.context.baseline.mean.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">StdDev:</span>{' '}
                                <span className="text-gray-900 dark:text-white">{selectedAnomaly.context.baseline.stdDev.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">P50:</span>{' '}
                                <span className="text-gray-900 dark:text-white">{selectedAnomaly.context.baseline.p50.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">P95:</span>{' '}
                                <span className="text-gray-900 dark:text-white">{selectedAnomaly.context.baseline.p95.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">P99:</span>{' '}
                                <span className="text-gray-900 dark:text-white">{selectedAnomaly.context.baseline.p99.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* AI Analysis */}
                        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
                          <div className="mb-2 flex items-center gap-2">
                            <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            <h4 className="text-sm font-medium text-purple-900 dark:text-purple-300">
                              {t('anomaly.details.aiAnalysis', 'AI Analysis')}
                            </h4>
                          </div>
                          {analyzing ? (
                            <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              {t('anomaly.details.analyzing', 'Analyzing...')}
                            </div>
                          ) : selectedAnomaly.aiAnalysis ? (
                            <div className="space-y-3 text-sm">
                              <div>
                                <label className="font-medium text-purple-900 dark:text-purple-300">
                                  {t('anomaly.details.summary', 'Summary')}
                                </label>
                                <p className="text-purple-800 dark:text-purple-200">{selectedAnomaly.aiAnalysis.summary}</p>
                              </div>
                              <div>
                                <label className="font-medium text-purple-900 dark:text-purple-300">
                                  {t('anomaly.details.rootCause', 'Root Cause')}
                                </label>
                                <p className="text-purple-800 dark:text-purple-200">{selectedAnomaly.aiAnalysis.rootCause}</p>
                              </div>
                              <div>
                                <label className="font-medium text-purple-900 dark:text-purple-300">
                                  {t('anomaly.details.impact', 'Impact')}
                                </label>
                                <p className="text-purple-800 dark:text-purple-200">{selectedAnomaly.aiAnalysis.impact}</p>
                              </div>
                              {selectedAnomaly.aiAnalysis.recommendations?.length > 0 && (
                                <div>
                                  <label className="font-medium text-purple-900 dark:text-purple-300">
                                    {t('anomaly.details.recommendations', 'Recommendations')}
                                  </label>
                                  <ul className="list-inside list-disc text-purple-800 dark:text-purple-200">
                                    {selectedAnomaly.aiAnalysis.recommendations.map((rec, i) => (
                                      <li key={i}>{rec}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              <div className="flex items-center gap-2 pt-2 text-xs text-purple-600 dark:text-purple-400">
                                {t('anomaly.details.confidence', 'Confidence')}: {Math.round((selectedAnomaly.aiAnalysis.confidenceScore || 0) * 100)}%
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-purple-600 dark:text-purple-400">
                              {t('anomaly.details.noAnalysis', 'Click to analyze with AI')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                        onClick={() => setShowDetailsModal(false)}
                      >
                        {tCommon('close')}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Configuration Modal */}
        <Transition appear show={showConfigModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowConfigModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-gray-800">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                      {t('anomaly.config.title', 'Anomaly Detection Settings')}
                    </Dialog.Title>

                    {config && (
                      <div className="mt-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-900 dark:text-white">
                            {t('anomaly.config.enabled', 'Enable Detection')}
                          </label>
                          <Switch
                            checked={config.enabled}
                            onChange={(value) => setConfig({ ...config, enabled: value })}
                            className={`${
                              config.enabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                            } relative inline-flex h-6 w-11 items-center rounded-full`}
                          >
                            <span className={`${config.enabled ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
                          </Switch>
                        </div>

                        <div className="border-t pt-4 dark:border-gray-700">
                          <h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
                            {t('anomaly.config.methods', 'Detection Methods')}
                          </h4>
                          <div className="space-y-2">
                            {[
                              { key: 'useZScore', label: 'Z-Score Analysis' },
                              { key: 'useMovingAverage', label: 'Moving Average' },
                              { key: 'usePercentile', label: 'Percentile-based' },
                              { key: 'useEWMA', label: 'EWMA' },
                            ].map(({ key, label }) => (
                              <div key={key} className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                                <Switch
                                  checked={(config as any)[key]}
                                  onChange={(value) => setConfig({ ...config, [key]: value })}
                                  className={`${
                                    (config as any)[key] ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                                  } relative inline-flex h-5 w-9 items-center rounded-full`}
                                >
                                  <span className={`${(config as any)[key] ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition`} />
                                </Switch>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t pt-4 dark:border-gray-700">
                          <h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
                            {t('anomaly.config.metrics', 'Monitored Metrics')}
                          </h4>
                          <div className="space-y-2">
                            {[
                              { key: 'monitorResponseTime', label: t('anomaly.config.responseTime', 'Response Time') },
                              { key: 'monitorErrorRate', label: t('anomaly.config.errorRate', 'Error Rate') },
                              { key: 'monitorThroughput', label: t('anomaly.config.throughput', 'Throughput') },
                              { key: 'monitorLatencyP99', label: t('anomaly.config.latencyP99', 'P99 Latency') },
                            ].map(({ key, label }) => (
                              <div key={key} className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                                <Switch
                                  checked={(config as any)[key]}
                                  onChange={(value) => setConfig({ ...config, [key]: value })}
                                  className={`${
                                    (config as any)[key] ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                                  } relative inline-flex h-5 w-9 items-center rounded-full`}
                                >
                                  <span className={`${(config as any)[key] ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition`} />
                                </Switch>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t pt-4 dark:border-gray-700">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('anomaly.config.zScoreThreshold', 'Z-Score Threshold')}
                              </label>
                              <input
                                type="number"
                                step="0.5"
                                value={config.zScoreThreshold}
                                onChange={(e) => setConfig({ ...config, zScoreThreshold: parseFloat(e.target.value) })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('anomaly.config.cooldown', 'Cooldown (min)')}
                              </label>
                              <input
                                type="number"
                                value={config.cooldownMinutes}
                                onChange={(e) => setConfig({ ...config, cooldownMinutes: parseInt(e.target.value) })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('anomaly.config.baselineDays', 'Baseline Period (days)')}
                              </label>
                              <input
                                type="number"
                                value={config.baselinePeriodDays}
                                onChange={(e) => setConfig({ ...config, baselinePeriodDays: parseInt(e.target.value) })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('anomaly.config.minDataPoints', 'Min Data Points')}
                              </label>
                              <input
                                type="number"
                                value={config.minDataPoints}
                                onChange={(e) => setConfig({ ...config, minDataPoints: parseInt(e.target.value) })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                        onClick={() => setShowConfigModal(false)}
                      >
                        {tCommon('cancel')}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                        onClick={() => config && handleSaveConfig(config)}
                      >
                        {tCommon('save')}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </MainLayout>
  );
}
